import { css, html } from 'https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js'

/**
 * Skio Extension Component
 *
 * This can be used to extend the base Skio Plan Picker component
 * By doing this you can add custom functionality or style options to the original component (mostly useful for app block)
 * By adding the function name from the parent component you can overwrite the original function
 *
 * Bescher: adds the Welcome Kit benefits checklist and kit grid inside
 * subscription options. Data comes from window.BescherWelcomeKit
 * (see snippets/welcome-kit-data.liquid) because shadow DOM cannot reach
 * Liquid or theme CSS.
 *
 * Also draws two plan-card badges ("Best value" and a savings pill) in CSS
 * only, from text set in theme settings.
 *
 * Only groupContent() is overridden. Everything else — pricing, plan
 * selection, discounts, the frequency dropdown — is Skio's own code, untouched.
 */

class SkioExtensionComponent extends SkioPlanPicker {
  static styles = [
    super.styles,
    css`
      .skio-welcome-kit {
        margin-top: 0.75rem;
      }

      .skio-welcome-kit__benefits {
        list-style: none;
        margin: 2rem 0;
        padding: 0;
      }

      .skio-welcome-kit__benefits li {
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
        margin: 0 0 0.4rem 0;
        font-size: 14px;
        font-weight: 400;
        line-height: 1.4;
        color: var(--skio-group-text-color, #000);
      }

      .skio-welcome-kit__check {
        flex: 0 0 auto;
        width: 14px;
        height: 14px;
        margin-top: 2px;
        color: var(--skio-discount-color, #0fa573);
      }

      /* A benefit line like the others — the tick and spacing come from the li. */
      .skio-welcome-kit__more {
        color: inherit;
        text-decoration: underline;
      }

      .skio-welcome-kit__heading {
        margin: 0.9rem 0 1.5rem 0;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--skio-group-text-color, #000);
      }

      /* Flex, not grid: fixed-width tiles pack against each other from the
         left and wrap when they run out of room. 1fr grid columns stretched
         to fill the row, which pushed the tiles apart. */
      .skio-welcome-kit__grid {
        display: flex;
        flex-wrap: wrap;
        gap: 0.6rem;
        margin-bottom: 2rem;
        row-gap: 2rem;
      }

      /* fixed at the image width so the caption wraps to 80px */
      .skio-welcome-kit__tile {
        flex: 0 0 80px;
        width: 80px;
        text-align: center;
        overflow-wrap: break-word;
      }

      .skio-welcome-kit__media {
        display: flex;
        align-items: center;
        justify-content: center;
        aspect-ratio: 1 / 1;
        width: 100%;
        max-width: 80px;
        margin: 0 0 0.35rem;
        border-radius: 8px;
        background: rgba(0, 0, 0, 0.04);
        overflow: hidden;
      }

      .skio-welcome-kit__media img {
        max-width: 80px;
        max-height: 80px;
        width: 100%;
        height: 100%;
        object-fit: contain;
      }

      .skio-welcome-kit__price {
        font-size: 11px;
        line-height: 1.3;
        color: var(--skio-discount-color, #0fa573);
      }

      .skio-welcome-kit__title {
        font-size: 11px;
        line-height: 1.3;
        color: var(--skio-group-text-color, #000);
      }

      /* --- plan-card badges ---------------------------------------------------
         Pure CSS, so nothing in Skio's render has to change. Both hang off the
         .savings span in the card title: Skio only ever puts it in a
         subscription title, never the one-time one, so neither badge can land
         on one-time. Text arrives as CSS custom properties from
         skio-app-block.liquid; with no text, content resolves to none and the
         badge is not drawn at all. */

      /* Straddles the top border rather than sitting in the corner, where it
         would cover the price. Cards are 1rem apart, so it clears the one above. */
      .group-container:has(.group-title .savings)::before {
        content: var(--bescher-best-value-text, none);
        position: absolute;
        top: 0;
        right: 16px;
        z-index: 1;
        transform: translateY(-50%);
        padding: 2px 10px;
        border-radius: 999px;
        background: #3b5947;
        color: #fff;
        font-size: 10px;
        font-weight: 700;
        line-height: 1.4;
        letter-spacing: 0.08em;
        white-space: nowrap;
        pointer-events: none;
      }

      /* Room for the "Best value" tag, subscription card only. The values are
         set in skio-app-block.liquid only while the tag has text, so with no
         tag these fall back to Skio's own spacing. */
      .group-container:has(.group-title .savings) {
        margin-top: var(--bescher-best-value-card-margin, 0);
      }

      .group-container:has(.group-title .savings) .group-label {
        padding-top: var(--bescher-best-value-label-padding, 10px);
      }

      .group-title .savings::after {
        content: var(--bescher-save-badge-text, none);
        display: inline-block;
        margin-left: 8px;
        padding: 6px 12px;
        border-radius: 99px;
        background: linear-gradient(90deg, #dbb15c 0%, #fde9bf 50%, #dbb15c 100%);
        color: #3b5947;
        font-size: 11px;
        font-weight: 800;
        line-height: 1;
        letter-spacing: 0.04em;
        vertical-align: middle;
        white-space: nowrap;
        position: relative;
        top: -2px;
      }

      @media screen and (max-width: 749px) {
        .group-title .savings::after {
          margin-left: 0;
          margin-top: 0;
          top: 0;
        }

        .selling-plan-dropdown {
          margin-top: 10px;
        }
      }

      /* Text-only strip at the bottom of the subscription card. Deliberately
         flat — no arrow, no pill, no interaction — because it is display only. */
      .skio-subscription-banner {
        margin: 0.75rem 0 0.75rem;
        padding: 12px 16px;
        border-radius: 6px;
        background: rgba(59, 89, 71, 0.08);
        color: #3b5947;
        font-size: 11px;
        font-weight: 700;
        line-height: 1.4;
        letter-spacing: 0.08em;
        text-align: center;
        text-transform: uppercase;
      }

      .group-title {
        font-weight: 500;
      }

      /* Deliberately not .group-content: Skio hides that for any group that is
         not selected, which is what kept the kit out of sight on one-time. */
      .skio-welcome-kit-wrap.margin-left {
        margin-left: 25px;
      }

      /* "Always" mode: undo Skio's hide of .group-content (line 180 of the
         parent), so the frequency dropdown shows next to the kit even while
         one-time is selected. The attribute is set in connectedCallback, so
         "Only when selected" keeps Skio's behaviour untouched. */
      :host([welcome-kit-always]) .group-container:not(.group-container--selected) .group-content {
        pointer-events: auto;
        opacity: 1;
        max-height: 1000px;
        visibility: visible;
      }
    `,
  ]

  static properties = {
    ...super.properties,
  }

  constructor() {
    super()
  }

  connectedCallback() {
    super.connectedCallback()

    /* Drives the CSS above. An attribute rather than a class so Skio's own
       class handling on the host is left alone. */
    this.toggleAttribute('welcome-kit-always', !!this.welcomeKit?.alwaysVisible)
  }

  /**
   * Returns the Welcome Kit payload, or null when there is nothing to show.
   * Fails safe: any missing or malformed data renders the stock picker.
   */
  get welcomeKit() {
    const kit = window.BescherWelcomeKit

    if (!kit || !kit.enabled) return null

    const benefits = Array.isArray(kit.benefits) ? kit.benefits.filter(benefit => typeof benefit === 'string' && benefit.trim() !== '') : []
    const displayItems = Array.isArray(kit.displayItems) ? kit.displayItems.filter(item => item && (item.title || item.image)) : []

    if (!benefits.length && !displayItems.length) return null

    const link = kit.moreInfo
    const moreInfo =
      link && typeof link.url === 'string' && link.url.trim() !== ''
        ? { url: link.url, text: typeof link.text === 'string' && link.text.trim() !== '' ? link.text : 'More info' }
        : null

    return {
      benefits,
      displayItems,
      moreInfo,
      heading: typeof kit.heading === 'string' ? kit.heading : '',
      alwaysVisible: kit.alwaysVisible !== false,
    }
  }

  welcomeKitCheckIcon() {
    return html`
      <svg class="skio-welcome-kit__check" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
        <path d="M2.5 8.5L6 12L13.5 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    `
  }

  welcomeKitTemplate() {
    const kit = this.welcomeKit

    if (!kit) return ''

    return html`
      <div class="skio-welcome-kit">
        ${kit.benefits.length || kit.moreInfo
          ? html`
              <ul class="skio-welcome-kit__benefits">
                ${kit.benefits.map(benefit => html`
                  <li>${this.welcomeKitCheckIcon()}<span>${benefit}</span></li>
                `)}
                ${kit.moreInfo
                  ? html`
                      <li>
                        ${this.welcomeKitCheckIcon()}
                        <a
                          class="skio-welcome-kit__more"
                          href=${kit.moreInfo.url}
                          @click=${event => event.stopPropagation()}
                        >${kit.moreInfo.text}</a>
                      </li>
                    `
                  : ''}
              </ul>
            `
          : ''}
        ${kit.displayItems.length
          ? html`
              ${kit.heading ? html`<div class="skio-welcome-kit__heading">${kit.heading}</div>` : ''}

              <div class="skio-welcome-kit__grid">
                ${kit.displayItems.map(item => html`
                  <div class="skio-welcome-kit__tile">
                    <div class="skio-welcome-kit__media">
                      ${item.image ? html`<img src=${item.image} alt=${item.alt || item.title || ''} loading="lazy" />` : ''}
                    </div>
                    ${item.price ? html`<div class="skio-welcome-kit__price"><s>${item.price}</s></div>` : ''}
                    ${item.title ? html`<div class="skio-welcome-kit__title">${item.title}</div>` : ''}
                  </div>
                `)}
              </div>
            `
          : ''}
      </div>
    `
  }

  /** Text-only banner for the bottom of the subscription card, or '' when unset. */
  subscriptionBannerTemplate() {
    const text = window.BescherSubscriptionBanner

    if (typeof text !== 'string' || text.trim() === '') return ''

    return html`<div class="skio-subscription-banner">${text}</div>`
  }

  /**
   * Skio calls this for subscription groups only (the one-time option never
   * reaches it). Carries the Welcome Kit and the bottom banner; visibility
   * follows the "Show benefits and grid" theme setting.
   */
  groupContent(group) {
    const base = super.groupContent(group)

    if (!group) return base

    const kit = this.welcomeKitTemplate()
    const banner = this.subscriptionBannerTemplate()

    if (!kit && !banner) return base

    const content = html`${kit}${banner}`
    const indent = this.options?.show_radio_selector ? 'margin-left' : ''
    /* With the kit off, the banner alone takes the "always" path. */
    const alwaysVisible = this.welcomeKit ? this.welcomeKit.alwaysVisible : true

    /* Only when selected: reuse Skio's .group-content, which Skio hides for
       any group that is not selected. Every group can carry the kit, because
       only the selected one is ever visible. */
    if (!alwaysVisible) {
      return html`
        ${base}
        <div class="group-content ${indent}">${content}</div>
      `
    }

    /* Always: our own class, which Skio's hide rule does not cover. Vertical
       layout calls this once per subscription group (Subscription, Prepaid,
       ...), and a kit that never hides would repeat, so only the first group
       carries it. Horizontal layout calls this once and needs no guard. */
    const groups = this.availableSellingPlanGroups || []

    if (this.options?.layout == 'vertical' && groups.length && groups[0] !== group) return base

    return html`
      ${base}
      <div class="skio-welcome-kit-wrap ${indent}">${content}</div>
    `
  }

  updated(changed) {
    super.updated(changed)

    if (changed.has('selectedSellingPlan') || changed.has('selectedVariant')) this.syncAfterpayAmount()
  }

  /**
   * Afterpay's messaging block reads the product's one-time price, so on a
   * subscription it quotes 4 payments of the wrong amount. Its <square-placement>
   * element redraws when data-amount changes, so we keep that in step with the
   * price Skio is showing. Plain dollars, e.g. "103.20".
   *
   * Scoped to this picker's section so a second product elsewhere on the page
   * keeps its own amount.
   */
  syncAfterpayAmount() {
    try {
      if (!this.selectedVariant) return

      /* discount() is 0 with no plan, so this is the one-time price on one-time. */
      const cents = this.price(this.selectedSellingPlan, false)

      if (typeof cents !== 'number' || !isFinite(cents)) return

      const amount = (cents / 100).toFixed(2)
      const scope = this.closest('.shopify-section') || document

      scope.querySelectorAll('square-placement').forEach(placement => {
        if (placement.getAttribute('data-amount') !== amount) placement.setAttribute('data-amount', amount)
      })
    } catch (e) {
      /* messaging only — never let it break the picker */
    }
  }
}

customElements.define('skio-plan-picker-custom', SkioExtensionComponent)
